import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

export type Exclusion = {
  id: number
  value: string
  label: string | null
  network: boolean
}

type Props = {
  exclusions: Exclusion[]
  currentIp: string
  currentIpExcluded: boolean
}

// The "don't count me" list. It sits on the Analytics page because that is
// where the number it corrects is read: a visit from a listed address or range
// is dropped at /api/collect and never becomes a row.
export function TrafficExclusions({ exclusions, currentIp, currentIpExcluded }: Props) {
  const { props } = usePage<PageProps>()
  const [value, setValue] = useState("")
  const [label, setLabel] = useState("")
  const error = props.errors?.value

  function add(newValue: string, newLabel: string) {
    if (!newValue.trim()) return
    router.post(
      "/admin/analytics/exclusions",
      { pulse_exclusion: { value: newValue.trim(), label: newLabel.trim() || null } },
      {
        preserveScroll: true,
        onSuccess: () => {
          setValue("")
          setLabel("")
        },
      },
    )
  }

  function remove(exclusion: Exclusion) {
    router.delete(`/admin/analytics/exclusions/${exclusion.id}`, { preserveScroll: true })
  }

  return (
    <div className="rounded-lg border border-hairline bg-surface">
      <div className="border-b border-hairline px-5 py-3">
        <h2>Traffic not counted</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Visits from these addresses are dropped before they are recorded — the way to keep your
          own browsing out of the figures. A whole connection can be excluded at once, or a range
          in CIDR form such as <code>86.1.224.0/24</code>.
        </p>
      </div>

      <div className="px-5 py-4">
        {currentIpExcluded ? (
          <p className="text-sm text-ink-muted">
            This device’s connection (<code>{currentIp}</code>) is already excluded.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" size="sm" onClick={() => add(currentIp, "This connection")}>
              Stop counting this connection
            </Button>
            <span className="text-sm text-ink-muted">
              <code>{currentIp}</code> — every device on it, including phones.
            </span>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem] flex-1">
            <label htmlFor="exclusion-value">Address or range</label>
            <Input
              id="exclusion-value"
              value={value}
              placeholder="86.1.224.243"
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  add(value, label)
                }
              }}
            />
          </div>
          <div className="min-w-[12rem] flex-1">
            <label htmlFor="exclusion-label">What is it? (optional)</label>
            <Input
              id="exclusion-label"
              value={label}
              placeholder="Home broadband"
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  add(value, label)
                }
              }}
            />
          </div>
          <Button variant="secondary" onClick={() => add(value, label)} disabled={!value.trim()}>
            Add
          </Button>
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}

        {exclusions.length > 0 && (
          <ul className="mt-4 divide-y divide-hairline border-t border-hairline">
            {exclusions.map((exclusion) => (
              <li key={exclusion.id} className="flex items-start justify-between gap-4 py-2">
                <span className="text-sm text-ink-body">
                  <code>{exclusion.value}</code>
                  {exclusion.network && <span className="ml-2 text-xs text-ink-muted">range</span>}
                  {exclusion.label && <span className="ml-2 text-ink-muted">{exclusion.label}</span>}
                </span>
                <Button variant="ghost" size="sm" className="shrink-0" onClick={() => remove(exclusion)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-4 text-sm text-ink-muted">
          On a phone away from an excluded connection, open any page of the site with{" "}
          <code>?pulse=off</code> on the end of the address. That browser stops being counted until
          you visit with <code>?pulse=on</code>. Signing in as an admin already stops the tracker
          loading at all.
        </p>
      </div>
    </div>
  )
}
